import {Attributes, Button, Separator} from 'platform/components';
import {Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {getNewVehicleEnumLabel, useNewVehicleCatalogueLabels} from '@dms/features/new-vehicle';

import {EMPTY_PLACEHOLDER, parseDate, type Nullish} from 'shared/web';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {isCalendarDate, joinParts, t} from '../utils/catalogueUi';
import {
  restrictionLabel,
  trimPriceRows,
  type DraftIssue,
  type NewModelDraft,
} from '../utils/newModelDraft';
import type {CatalogueChoice, ModelChoice} from './NewModelSteps';
import {PriceWithGross} from './PriceWithGross';
import {TrimPrices} from './TrimPrices';

type NewModelReviewProps = {
  draft: NewModelDraft;
  makes: CatalogueChoice[];
  models: ModelChoice[];
  currency: string;
  blockers: DraftIssue[];
  goTo: (step: number) => void;
};

type ReviewSectionProps = {
  step: number;
  title: string;
  onEdit: (step: number) => void;
  children: React.ReactNode;
};

function ReviewSection(props: ReviewSectionProps) {
  return (
    <VStack spacing={4}>
      <Separator spacing={0} />
      <HStack align="center" justify="space-between" spacing={3}>
        <Heading size={4}>{props.title}</Heading>
        <Button
          variant="link"
          title={t('general.actions.edit', 'Edit')}
          aria-label={t('general.actions.editNamed', 'Edit {name}', {
            name: props.title.replace(/ \(\d+.*\)$/, ''),
          })}
          onClick={() => props.onEdit(props.step)}
        />
      </HStack>
      {props.children}
    </VStack>
  );
}

function ReviewMoney(props: {amount: string; currency: string; vatRate: string | Nullish}) {
  return props.amount ? (
    <PriceWithGross amount={props.amount} currency={props.currency} vatRate={props.vatRate} />
  ) : (
    <Text size="small">{EMPTY_PLACEHOLDER}</Text>
  );
}

const EQUIPMENT_KIND_BY_TYPE = new Map([
  ['factory-option', 'option'],
  ['dealer-accessory', 'accessory'],
  ['wheel', 'wheel'],
]);

const valueOrPlaceholder = (value: string) => value || EMPTY_PLACEHOLDER;

export function NewModelReview(props: NewModelReviewProps) {
  const labels = useNewVehicleCatalogueLabels();
  const vatRate = useCatalogueVatRate();
  const formatDate = useDateTimeFormatter();
  // The date fields' own format, so the summary reads like what the author entered.
  const dateOrPlaceholder = (value: string | null) =>
    value && isCalendarDate(value)
      ? formatDate('dateShort', parseDate(value))
      : valueOrPlaceholder(value ?? '');
  const makeNames = new Map(props.makes.map((row) => [row.id, row.name]));
  const modelNames = new Map(props.models.map((row) => [row.id, row.name]));
  const trimNames = new Map(props.draft.trims.map((row) => [row.id, row.name]));
  const engineNames = new Map(props.draft.engines.map((row) => [row.id, row.name]));
  const featureNames = new Map(props.draft.equipment.map((row) => [row.id, row.name]));
  const makeName =
    props.draft.make.mode === 'new'
      ? props.draft.make.name
      : (makeNames.get(props.draft.make.id) ?? EMPTY_PLACEHOLDER);
  const modelName =
    props.draft.model.mode === 'new'
      ? props.draft.model.name
      : (modelNames.get(props.draft.model.id) ?? EMPTY_PLACEHOLDER);
  const isReady = props.blockers.length === 0;

  return (
    <VStack spacing={5}>
      <VStack spacing={3}>
        <HStack align="center" spacing={2}>
          <Show when={isReady}>
            <Icon value="navigation/check" color="text.success" size={5} />
          </Show>
          <Heading size={4}>
            {isReady
              ? t('entity.newVehicle.authoring.readyToCreate', 'Ready to create the draft')
              : t(
                  'entity.newVehicle.authoring.completeBeforeCreate',
                  'Complete these before creating the draft'
                )}
          </Heading>
        </HStack>
        <Show when={!isReady}>
          <VStack spacing={2}>
            {props.blockers.map((blocker, index) => (
              <HStack
                key={`${blocker.step}:${blocker.message}:${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                align="center"
                justify="space-between"
                spacing={3}
                wrap
              >
                <Text size="small">{blocker.message}</Text>
                <Button
                  variant="link"
                  title={t('entity.newVehicle.authoring.goToStep', 'Go to step {step}', {
                    step: blocker.step,
                  })}
                  onClick={() => props.goTo(blocker.step)}
                />
              </HStack>
            ))}
          </VStack>
        </Show>
      </VStack>

      <ReviewSection
        step={1}
        title={t('entity.newVehicle.authoring.identityReview', 'Make → Model → Model year')}
        onEdit={props.goTo}
      >
        <Attributes
          size="third"
          rows={[
            {label: t('entity.vehicle.labels.make', 'Make'), value: makeName},
            {label: t('entity.vehicle.labels.model', 'Model'), value: modelName},
            {
              label: t('entity.newVehicle.authoring.familyGroup', 'Family group'),
              value: valueOrPlaceholder(props.draft.model.modelFamily),
            },
            {
              label: t('entity.newVehicle.authoring.tsn', 'Type key (TSN)'),
              value: valueOrPlaceholder(props.draft.model.tsn),
            },
            {
              label: t('entity.newVehicle.labels.modelYear', 'Model year'),
              value: valueOrPlaceholder(props.draft.year.code),
            },
            {
              label: t('entity.newVehicle.authoring.valid', 'Valid'),
              value: `${dateOrPlaceholder(props.draft.year.validFrom)} → ${dateOrPlaceholder(props.draft.year.validTo)}`,
            },
            {
              label: t('entity.newVehicle.catalogue.catalogueName', 'Catalogue name'),
              value: valueOrPlaceholder(props.draft.year.catalogueName),
            },
            {
              label: t('entity.newVehicle.catalogue.pricelistVersionLabel', 'Price list version'),
              value: valueOrPlaceholder(props.draft.year.pricelistVersion),
            },
          ]}
        />
      </ReviewSection>

      <ReviewSection
        step={4}
        title={`${t('entity.newVehicle.catalogue.engines', 'Engines')} (${props.draft.engines.length})`}
        onEdit={props.goTo}
      >
        {props.draft.engines.map((row) => (
          <Text key={row.id} size="small">
            {joinParts([
              row.name,
              row.powerKw && `${row.powerKw} kW`,
              row.fuel && labels.formatFuel(row),
              row.transmission && labels.getTransmission(row.transmission),
              row.drive && labels.getDrive(row.drive),
            ])}
          </Text>
        ))}
      </ReviewSection>

      <ReviewSection
        step={5}
        title={`${t('entity.newVehicle.catalogue.trimLevels', 'Trim levels')} (${props.draft.trims.length})`}
        onEdit={props.goTo}
      >
        {props.draft.trims.map((row) => (
          <HStack key={row.id} align="center" justify="space-between" spacing={3} wrap>
            <Text size="small">
              {joinParts([
                row.name,
                row.bodyStyle && labels.getBodyStyle(row.bodyStyle),
                props.draft.variants
                  .filter((variant) => variant.trimId === row.id)
                  .map((variant) => engineNames.get(variant.engineId))
                  .filter((name): name is string => Boolean(name))
                  .join(', ') || undefined,
              ])}
            </Text>
            <ReviewMoney amount={row.basePriceNet} currency={props.currency} vatRate={vatRate} />
          </HStack>
        ))}
      </ReviewSection>

      <ReviewSection
        step={6}
        title={`${t('entity.newVehicle.authoring.colors', 'Colours')} (${props.draft.colours.length})`}
        onEdit={props.goTo}
      >
        {props.draft.colours.map((row) => (
          <HStack key={row.id} align="center" justify="space-between" spacing={3} wrap>
            <Text size="small">
              {joinParts([row.name, row.kind && labels.getColorType(row.kind)])}
            </Text>
            <TrimPrices
              rows={trimPriceRows(row.trimIds, row.trimPrices)}
              trims={props.draft.trims}
              currency={props.currency}
              vatRate={vatRate}
              canViewCost={false}
            />
          </HStack>
        ))}
      </ReviewSection>

      <ReviewSection
        step={7}
        title={`${t('entity.newVehicle.catalogue.upholstery', 'Upholstery')} (${props.draft.upholsteries.length})`}
        onEdit={props.goTo}
      >
        {props.draft.upholsteries.map((row) => (
          <HStack key={row.id} align="center" justify="space-between" spacing={3} wrap>
            <Text size="small">
              {joinParts([row.name, row.fabric && labels.getInteriorMaterial(row.fabric)])}
            </Text>
            <ReviewMoney amount={row.priceNet} currency={props.currency} vatRate={vatRate} />
          </HStack>
        ))}
        {props.draft.restrictions.map((restriction) => (
          <Text
            key={`${restriction.colourId}:${restriction.upholsteryId}`}
            size="small"
            color="secondary"
          >
            {restrictionLabel(props.draft, restriction)}
          </Text>
        ))}
      </ReviewSection>

      <ReviewSection
        step={8}
        title={`${t('entity.newVehicle.catalogue.equipment', 'Equipment')} (${props.draft.attachments.length} ${t('entity.newVehicle.authoring.attachments', 'attachments')})`}
        onEdit={props.goTo}
      >
        {props.draft.equipment.map((row) => (
          <Text key={row.id} size="small">
            {joinParts([
              row.name,
              getNewVehicleEnumLabel(
                'featureKind',
                EQUIPMENT_KIND_BY_TYPE.get(row.equipmentType) ?? 'option',
                t
              ),
              row.standaloneOrderable
                ? undefined
                : t('entity.newVehicle.authoring.packageOnly', 'Package-only'),
              props.draft.attachments
                .filter((attachment) => attachment.featureId === row.id)
                .map((attachment) => {
                  const trimName = trimNames.get(attachment.trimId);
                  return trimName
                    ? `${trimName}: ${getNewVehicleEnumLabel('inclusion', attachment.inclusion, t)}`
                    : undefined;
                })
                .filter((part): part is string => Boolean(part))
                .join(', ') || t('entity.newVehicle.authoring.libraryOnlyShort', 'Library only'),
            ])}
          </Text>
        ))}
      </ReviewSection>

      <ReviewSection
        step={9}
        title={`${t('entity.newVehicle.catalogue.packages', 'Packages')} (${props.draft.packages.length})`}
        onEdit={props.goTo}
      >
        {props.draft.packages.map((row) => (
          <HStack key={row.id} align="center" justify="space-between" spacing={3} wrap>
            <Text size="small">
              {joinParts([
                row.name,
                row.memberFeatureIds
                  .map((id) => featureNames.get(id))
                  .filter((name): name is string => Boolean(name))
                  .join(', ') || undefined,
              ])}
            </Text>
            <TrimPrices
              rows={trimPriceRows(row.applicableTrimIds, row.trimPrices)}
              trims={props.draft.trims}
              currency={props.currency}
              vatRate={vatRate}
              canViewCost={false}
            />
          </HStack>
        ))}
      </ReviewSection>
    </VStack>
  );
}
